import { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, ReferenceLine,
} from 'recharts';
import { GitCompare, TrendingUp, TrendingDown, Target } from 'lucide-react';

export default function ComparisonModule() {
  const { simResult, realTrades, settings } = useApp();
  const capital = settings.simCapital || 500000;

  const realClosed = useMemo(() => realTrades.filter(t => t.status === 'CLOSED'), [realTrades]);

  // Build real equity curve
  const realEquityCurve = useMemo(() => {
    if (!realClosed.length) return [];
    const sorted = [...realClosed].sort((a, b) => new Date(a.closeDate) - new Date(b.closeDate));
    let equity = capital;
    const curve = [{ date: 'Start', equity }];
    sorted.forEach(t => {
      equity += (t.pnl || 0);
      curve.push({ date: t.closeDate?.slice(0, 10) || '', equity: Math.round(equity) });
    });
    return curve;
  }, [realClosed, capital]);

  // Merge curves for comparison chart
  const mergedCurve = useMemo(() => {
    const simCurve = simResult.equityCurve || [];
    if (!simCurve.length && !realEquityCurve.length) return [];

    const allDates = new Set([
      ...simCurve.map(p => p.date),
      ...realEquityCurve.map(p => p.date),
    ]);
    const dates = [...allDates].sort();

    let lastSim = capital, lastReal = capital;
    return dates.map(date => {
      const simPt = simCurve.find(p => p.date === date);
      const realPt = realEquityCurve.find(p => p.date === date);
      if (simPt) lastSim = simPt.equity;
      if (realPt) lastReal = realPt.equity;
      return { date: date?.slice?.(5) || date, sim: lastSim, real: lastReal };
    });
  }, [simResult.equityCurve, realEquityCurve, capital]);

  // Compute real metrics
  const realMetrics = useMemo(() => {
    const wins = realClosed.filter(t => t.pnl > 0);
    const losses = realClosed.filter(t => t.pnl <= 0);
    const winRate = realClosed.length > 0 ? Math.round((wins.length / realClosed.length) * 100) : 0;
    const avgWin = wins.length > 0 ? Math.round(wins.reduce((s, t) => s + t.pnl, 0) / wins.length) : 0;
    const avgLoss = losses.length > 0 ? Math.round(losses.reduce((s, t) => s + t.pnl, 0) / losses.length) : 0;
    const expectancy = realClosed.length > 0 ? Math.round(realClosed.reduce((s, t) => s + t.pnl, 0) / realClosed.length) : 0;
    const totalPnL = realClosed.reduce((s, t) => s + (t.pnl || 0), 0);

    let peak = capital, maxDD = 0;
    let eq = capital;
    realClosed.forEach(t => {
      eq += (t.pnl || 0);
      if (eq > peak) peak = eq;
      const dd = (peak - eq) / peak;
      if (dd > maxDD) maxDD = dd;
    });

    return { totalTrades: realClosed.length, winRate, avgWin, avgLoss, expectancy, maxDrawdown: Math.round(maxDD * 100), totalPnL };
  }, [realClosed, capital]);

  // Missed trades: signals that sim traded but real didn't
  const missedTrades = useMemo(() => {
    const realSymbols = new Set(realTrades.map(t => t.symbol));
    return (simResult.trades || []).filter(t => !realSymbols.has(t.symbol));
  }, [simResult.trades, realTrades]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="comparison-module">
      <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2 mb-1">
        <GitCompare className="w-6 h-6 text-trading-blue" />
        Comparison
      </h1>
      <p className="text-sm text-trading-muted mb-6">Simulator vs Real performance analysis</p>

      {/* Equity Curves */}
      <div className="bg-trading-surface border border-trading-border rounded-sm p-4 mb-6" data-testid="comparison-chart">
        <h3 className="text-xs uppercase tracking-widest text-trading-muted font-semibold mb-3">Equity Curves</h3>
        {mergedCurve.length > 1 ? (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={mergedCurve}>
              <CartesianGrid stroke="#242C3D" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 10 }} domain={['auto', 'auto']} />
              <Tooltip contentStyle={{ backgroundColor: '#10141D', border: '1px solid #242C3D', fontSize: 12 }} formatter={(val) => [`PHP ${Number(val).toLocaleString()}`]} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#94A3B8' }} />
              <ReferenceLine y={capital} stroke="#242C3D" strokeDasharray="5 5" />
              <Line type="monotone" dataKey="sim" stroke="#3B82F6" strokeWidth={2} dot={false} name="Simulator" />
              <Line type="monotone" dataKey="real" stroke="#10B981" strokeWidth={2} dot={false} name="Real" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[320px] flex items-center justify-center text-trading-muted text-sm">
            Need both simulator and real trades to compare.
          </div>
        )}
      </div>

      {/* Metrics Comparison */}
      <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden mb-6" data-testid="metrics-comparison">
        <div className="px-4 py-2 border-b border-trading-border bg-trading-elevated">
          <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold">Metrics Comparison</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr className="bg-trading-elevated/50 border-b border-trading-border">
                <th>Metric</th>
                <th className="text-trading-blue">Simulator</th>
                <th className="text-trading-green">Real</th>
                <th>Gap</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Total Trades', sim: simResult.metrics.totalTrades, real: realMetrics.totalTrades },
                { label: 'Win Rate', sim: `${simResult.metrics.winRate}%`, real: `${realMetrics.winRate}%`, simNum: simResult.metrics.winRate, realNum: realMetrics.winRate },
                { label: 'Expectancy', sim: `PHP ${simResult.metrics.expectancy.toLocaleString()}`, real: `PHP ${realMetrics.expectancy.toLocaleString()}`, simNum: simResult.metrics.expectancy, realNum: realMetrics.expectancy },
                { label: 'Avg Win', sim: `PHP ${simResult.metrics.avgWin.toLocaleString()}`, real: `PHP ${realMetrics.avgWin.toLocaleString()}` },
                { label: 'Avg Loss', sim: `PHP ${simResult.metrics.avgLoss.toLocaleString()}`, real: `PHP ${realMetrics.avgLoss.toLocaleString()}` },
                { label: 'Max Drawdown', sim: `${simResult.metrics.maxDrawdown}%`, real: `${realMetrics.maxDrawdown}%` },
              ].map(row => (
                <tr key={row.label} className="border-b border-trading-border/50">
                  <td className="text-trading-muted">{row.label}</td>
                  <td className="text-trading-blue">{row.sim}</td>
                  <td className="text-trading-green">{row.real}</td>
                  <td>
                    {row.simNum !== undefined && row.realNum !== undefined && (
                      <span className={row.realNum >= row.simNum ? 'text-trading-green' : 'text-trading-red'}>
                        {row.realNum >= row.simNum ? '+' : ''}{row.realNum - row.simNum}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Missed Trades */}
      <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden" data-testid="missed-trades">
        <div className="px-4 py-2 border-b border-trading-border bg-trading-elevated">
          <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold">
            Missed Trades ({missedTrades.length})
          </span>
        </div>
        {missedTrades.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr className="bg-trading-elevated/50 border-b border-trading-border">
                  <th>Symbol</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>P&L</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {missedTrades.slice(0, 20).map((t, i) => (
                  <tr key={i} className="border-b border-trading-border/50">
                    <td className="text-trading-text font-semibold">{t.symbol}</td>
                    <td>{t.entryPrice?.toFixed(2)}</td>
                    <td>{t.exitPrice?.toFixed(2)}</td>
                    <td className={t.pnl >= 0 ? 'text-trading-green' : 'text-trading-red'}>
                      {t.pnl >= 0 ? '+' : ''}{t.pnl?.toLocaleString()}
                    </td>
                    <td className="text-trading-muted text-xs">{t.exitReason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-trading-muted text-sm">
            {simResult.trades?.length ? 'All simulator trades were executed.' : 'No simulator trades to compare.'}
          </div>
        )}
      </div>
    </div>
  );
}
